/**
 * Define all of your application routes here
 * for more information on routes, see the
 * official documentation https://router.vuejs.org/en/
 */

import { Routes } from '@/helpers/consts';

const getPathName = path => (path.name || path.view).replace(/^\w+?\//, '');

const mapChildrenNames = (children, title) =>
  children.map(c => ({
    ...c,
    path: c.path.replace(/^\//, ''),
    name: getPathName(c),
    meta: { title },
  }));

const getTabbedView = (children, name) => {
  let path = children[0].path || '';

  children[0].path = '';
  path = path[0] === '/' ? path : '/' + path;

  const title = name || children[0].name || children[0].view;

  return {
    view: 'TabView',
    meta: { name, title },
    path,
    props: { children: children.filter(p => !p.hidden).map(getPathName) },
    children: mapChildrenNames(children, title).sort((a, _b) => (a.path ? 1 : -1)),
  };
};

const paths = [
  {
    path: Routes.overview,
    view: 'Overview',
    meta: { permission: null },
  },

  getTabbedView(
    [
      {
        path: Routes.reviewId,
        view: 'Reviews',
      },
      {
        path: Routes.products,
        view: 'Products',
      },
      {
        path: Routes.smartReviews,
        view: 'SmartReviews',
      },

      {
        path: Routes.questionId,
        view: 'Questions',
        meta: { permission: 'q_and_a' },
      },
      {
        path: Routes.linkProducts,
        view: 'LinkProducts',
        meta: { permission: 'link_products' },
      },
    ],
    'ReviewsAndQA'
  ),

  getTabbedView([
    {
      path: Routes.importReviewsId,
      view: 'ImportReviews',
      meta: { permission: null },
    },
    {
      path: Routes.shopReviews,
      name: 'ShopReviews',
      view: 'ShopReviews',
      meta: { permission: null },
    },
    {
      path: Routes.migrateReviews,
      name: 'MigrateReviews',
      view: 'MigrateReviews',
      meta: { permission: null },
    },
  ]),
  getTabbedView(
    [
      {
        path: Routes.requestSchedule,
        view: 'RequestSchedule',
        props: true,
      },
      {
        path: Routes.reviewRequestsConfiguration,
        view: 'ReviewRequestsConfiguration',
        props: true,
      },
      {
        path: Routes.unverifiedReviewSimpleEmail,
        view: 'Email/UnverifiedReviewSimpleEmail',
        name: 'UnverifiedReviewSimpleEmail',
      },
      {
        path: Routes.requestViaLink,
        view: 'RequestViaLink',
      },
    ],
    'EmailAndSmsRequests'
  ),

  getTabbedView(
    [
      {
        path: Routes.widgets,
        view: 'Widgets',
      },
      {
        path: Routes.customizeProduct,
        view: 'CustomizeProduct',
        hidden: true,
      },
      {
        path: Routes.customizePlugin,
        view: 'CustomizePlugin',
        hidden: true,
      },

      {
        path: Routes.customizeBadge,
        view: 'CustomizeBadge',
        hidden: true,
      },
      {
        path: Routes.allReviewsPage,
        view: 'AllReviews',
        hidden: true,
      },
      {
        path: Routes.customizeRequests,
        view: 'CustomizeRequests',
        hidden: true,
      },
      {
        path: Routes.customizeCarousel,
        view: 'CustomizeCarousel',
        hidden: true,
      },
      {
        path: Routes.customizeStars,
        view: 'CustomizeStars',
        hidden: true,
      },
      {
        path: Routes.customizeFloatingWidget,
        view: 'CustomizeFloatingWidget',
        hidden: true,
      },
      {
        path: Routes.advancedWidgets,
        view: 'AdvancedWidgets',
      },
      {
        path: Routes.settingsCustomCss,
        view: 'SettingsCustomCss',
      },
      {
        path: Routes.seo,
        view: 'Seo',
      },
    ],
    'Widgets'
  ),

  getTabbedView(
    [
      {
        path: Routes.integration,
        view: 'Integration',
        name: 'Integrations',
        children: [
          { view: 'Integrations', path: '' },
          {
            path: Routes.googleShoppingIntegration,
            view: 'GoogleShoppingIntegration',
          },
          {
            path: Routes.aftershipIntegration,
            view: 'AftershipIntegration',
          },
          {
            path: Routes.orderPrinterIntegration,
            view: 'OrderPrinterIntegration',
          },
          {
            path: Routes.orderPrinterProIntegration,
            view: 'OrderPrinterProIntegration',
          },
          {
            path: Routes.klaviyoIntegration,
            view: 'KlaviyoIntegration',
          },
          {
            path: Routes.shopifyFlow,
            view: 'ShopifyFlow',
          },
          {
            path: Routes.pageflyIntegration,
            view: 'PageflyIntegration',
          },
          {
            path: Routes.gorgiasIntegration,
            view: 'GorgiasIntegration',
          },
          {
            path: Routes.zendeskIntegration,
            view: 'ZendeskIntegration',
          },
          {
            path: Routes.omnisendIntegration,
            view: 'OmnisendIntegration',
          },
          {
            path: Routes.riseAiIntegration,
            view: 'RiseAiIntegration',
          },
          {
            path: Routes.smileIoIntegration,
            view: 'SmileIoIntegration',
          },
          {
            path: Routes.loyaltyLionIntegration,
            view: 'LoyaltyLionIntegration',
          },
          {
            path: Routes.salesforceIntegration,
            view: 'SalesforceIntegration',
          },
          {
            path: Routes.layouthubIntegration,
            view: 'LayouthubIntegration',
          },
          {
            path: Routes.weglotIntegration,
            view: 'WeglotIntegration',
          },
          {
            path: Routes.fastSimonIntegration,
            view: 'FastSimonIntegration',
          },
          {
            path: Routes.wiserIntegration,
            view: 'WiserIntegration',
          },
          {
            path: Routes.limespotIntegration,
            view: 'LimespotIntegration',
          },
          {
            path: Routes.gempagesIntegration,
            view: 'GempagesIntegration',
          },
          {
            path: Routes.carsonIntegration,
            view: 'CarsonIntegration',
          },
          {
            path: Routes.jsonLdForSeoIntegration,
            view: 'JsonLdForSeoIntegration',
          },
          {
            path: Routes.seoBoosterIntegration,
            view: 'SeoBoosterIntegration',
          },
          {
            path: Routes.rebuyIntegration,
            view: 'RebuyIntegration',
          },
          {
            path: Routes.seoAllInOneIntegration,
            view: 'SeoAllInOneIntegration',
          },
          {
            path: Routes.OberloIntegration,
            view: 'OberloIntegration',
          },
          {
            path: Routes.DropifiedIntegration,
            view: 'DropifiedIntegration',
          },
          {
            path: Routes.ImportifyIntegration,
            view: 'ImportifyIntegration',
          },
          {
            path: Routes.SpreadrIntegration,
            view: 'SpreadrIntegration',
          },
          {
            path: Routes.YoastIntegration,
            view: 'YoastIntegration',
          },
          {
            path: Routes.SeoImageIntegration,
            view: 'SeoImageIntegration',
          },
          {
            path: Routes.bazaarvoiceIntegration,
            view: 'BazaarvoiceIntegration',
          },
          {
            path: Routes.ZipifyIntegration,
            view: 'ZipifyIntegration',
          },
          {
            path: Routes.SchemaIntegration,
            view: 'SchemaIntegration',
          },
          {
            path: Routes.Agencies,
            view: 'Agencies',
          },
          {
            path: '/test-agency',
            view: 'TestAgency',
          },
          {
            path: Routes.HeyCarson,
            view: 'HeyCarson',
          },
          {
            path: Routes.PollardMedia,
            view: 'PollardMedia',
          },
          {
            path: Routes.BrandActive,
            view: 'BrandActive',
          },
          {
            path: Routes.BoldStrategies,
            view: 'BoldStrategies',
          },
          {
            path: Routes.FourSocial,
            view: 'FourSocial',
          },
          {
            path: Routes.HawkeMedia,
            view: 'HawkeMedia',
          },
          {
            path: Routes.AttentionGrabbing,
            view: 'AttentionGrabbing',
          },
          {
            path: Routes.Droomtech,
            view: 'Droomtech',
          },
          {
            path: Routes.RaysTechnology,
            view: 'RaysTechnology',
          },
          {
            path: Routes.ArrowIntegration,
            view: 'ArrowIntegration',
          },
          {
            path: Routes.BlueOwl,
            view: 'BlueOwl',
          },
          { path: '/', redirect: 'Integrations' },
        ],
      },
      {
        path: Routes.integrationFilter,
        view: 'IntegrationsFilter',
        name: 'IntegrationsCustomerSupport',
        props: { chosenCategory: 2 },
      },
      {
        path: Routes.integrationFilter,
        view: 'IntegrationsFilter',
        name: 'IntegrationsMarketing',
        props: { chosenCategory: 3 },
      },
      {
        path: Routes.integrationFilter,
        view: 'IntegrationsFilter',
        name: 'IntegrationsRecommendation',
        props: { chosenCategory: 4 },
      },
      {
        path: Routes.integrationFilter,
        view: 'IntegrationsFilter',
        name: 'IntegrationsPageBuilders',
        props: { chosenCategory: 6 },
      },
      {
        path: Routes.integrationFilter,
        view: 'IntegrationsFilter',
        name: 'IntegrationsProductImporters',
        props: { chosenCategory: 8 },
      },
      {
        path: Routes.integrationFilter,
        view: 'IntegrationsFilter',
        name: 'IntegrationsSeo',
        props: { chosenCategory: 5 },
      },
      {
        path: Routes.integrationFilter,
        view: 'IntegrationsFilter',
        name: 'IntegrationsShipping',
        props: { chosenCategory: 9 },
      },
      {
        path: Routes.integrationFilter,
        view: 'IntegrationsFilter',
        name: 'IntegrationsOthers',
        props: { chosenCategory: 7 },
      },
    ],
    'Integration'
  ),

  getTabbedView([
    {
      path: Routes.generalSettings,
      view: 'GeneralSettings',
    },
    {
      path: Routes.account,
      view: 'Account',
      props: route => ({ openPlanPicker: route.query.openPlanPicker }),
    },
    {
      path: Routes.analytics,
      view: 'Analytics',
    },
    {
      path: Routes.teamMembers,
      view: 'TeamMembers',
    },
    {
      path: Routes.linkShops,
      view: 'LinkShops',
    },
    {
      path: Routes.helpAndTutorials,
      view: 'HelpAndTutorials',
    },
  ]),
];

export const sections = paths.map(p => {
  const view = p.children ? p.children[0].name : p.name || p.view;

  return { view, name: p.meta.name || view, path: p.path };
});

export const shopifySections = sections.slice(1);

export default [
  {
    path: Routes.auth,
    view: 'Auth',
  },
  {
    path: Routes.welcome,
    view: 'Welcome',
    props: true,
  },

  ...paths,
  {
    path: Routes.countryReviewRequestEmail,
    view: 'Email/ReviewRequestEmail',
    name: 'ReviewRequestEmail',
  },

  {
    path: Routes.countryCouponEmail,
    view: 'Email/CouponEmail',
    name: 'CouponEmail',
  },
  {
    path: Routes.countryUnverifiedReviewConfirmationEmail,
    view: 'Email/UnverifiedReviewConfirmationEmail',
    name: 'UnverifiedReviewConfirmationEmail',
  },
  {
    path: Routes.countryPhotoRequestEmail,
    view: 'Email/PhotoRequestEmail',
    name: 'PhotoRequestEmail',
  },

  {
    path: Routes.countryThankYouPageTemplate,
    view: 'Email/ThankYouPageTemplate',
    name: 'ThankYouPageTemplate',
  },

  {
    path: Routes.orderPrinter,
    view: 'Email/OrderPrinterTemplate',
    name: 'OrderPrinterTemplate',
  },

  {
    path: Routes.printedReviewRequests,
    view: 'PrintedReviewRequests',
  },

  {
    path: Routes.customizeFacebook,
    view: 'CustomizeFacebook',
  },

  {
    path: Routes.integrationOld,
    view: 'IntegrationOld',
  },
  {
    path: Routes.customReviewForm,
    view: 'CustomReviewForm',
  },
  {
    path: Routes.group,
    view: 'GroupAdd',
  },
  {
    path: Routes.groupEdit,
    view: 'GroupEdit',
  },

  {
    path: Routes.pickTrialPlan,
    view: 'PickTrialPlan',
  },
  {
    path: Routes.trialEnded,
    view: 'TrialEnded',
  },

  {
    path: Routes.reviewImporterSettings,
    view: 'ReviewImporterSettings',
  },

  {
    path: Routes.getStarted,
    view: 'GetStarted',
  },

  {
    path: Routes.firstSteps,
    view: 'FirstSteps',
  },
  {
    path: Routes.planPick,
    view: 'PlanPick',
  },

  {
    path: Routes.requestEmailTemplate,
    view: 'RequestEmailTemplate',
  },
  {
    path: Routes.couponEmailTemplate,
    view: 'CouponEmailTemplate',
  },
  {
    path: Routes.photoEmailTemplate,
    view: 'PhotoEmailTemplate',
  },
  {
    path: Routes.thankYouEmailTemplate,
    view: 'ThankYouTemplate',
  },

  {
    path: Routes.installWizard,
    view: 'InstallWizard',
  },
  {
    path: Routes.requestWizard,
    view: 'RequestWizard',
  },

  {
    path: Routes.Digitlabs,
    view: 'Digitlabs',
  },

  {
    path: Routes.DsersIntegration,
    view: 'DsersIntegration',
  },
  {
    path: Routes.SeoImageCompression,
    view: 'SeoImageCompression',
  },
  {
    path: Routes.AutoQA,
    view: 'AutoQA',
  },

  {
    path: Routes.widgets,
    view: 'Widgets',
  },
  {
    path: Routes.templatesSMS,
    view: 'SMSTemplates',
  },

  {
    path: '*',
    redirect: { name: 'Overview' },
  },
];
